import { NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';

import { ACCESS_ROUTE } from 'app/access/access-routing.constant';
import { SamlInternalLogoutComponent } from 'app/access/components/saml-internal-logout/saml-internal-logout.component';
import { OauthGoogleHandlerComponent } from 'app/access/oauth/gooogle/google.component';
import { APP_ROUTE } from 'app/app-routing.constant';
import { AsyncAvatarLoadComponent } from 'app/avatar-preview-renderer/async-avatar-loader.component';
import { AuthGuard, UserRole } from 'app/core/authentication';
import { EmptyLayoutComponent } from 'app/core/layouts/empty-layout/empty-layout.component';
import { ExternalLinkGuard } from 'app/core/navigation';
import { DemoSiteGuard } from 'app/core/navigation/guards/demo-site-route.guard';
import { NovaExternalLinkGuard } from 'app/core/navigation/guards/nova-external-link.guard';
import { DASHBOARD_USAGE_ROUTE } from 'app/dashboard-usage/dashboard-usage-routing.constant';
import { PARENT_ROUTE } from 'app/parent/parent-routing.constant';
import { EDITOR_ROUTE } from 'app/routing-constants/editor-routing.constant';
import { ForbiddenComponent } from 'app/shared/components/forbidden/forbidden.component';
import { MergeCompleteComponent } from 'app/shared/components/merge-complete/merge-complete.component';
import { PageNotFoundComponent } from 'app/shared/components/page-not-found/page-not-found.component';
import { PartnerActivityComponent } from 'app/shared/components/partner-activity/partner-activity.component';
import { VerifyEmailComponent } from 'app/shared/components/verify-email/verify-email.component';
import { SharedLinksGuard } from 'app/teacher/guards/shared-links.guard';
import { TEACHERS_ROUTE } from 'app/teachers/teachers-routing.constant';

import { CalendarEditComponent } from './admin/calendar-edit/calendar-edit.component';

// defaults to prod setup.
const LOGIN_ROUTE_OBJ = {
  path: APP_ROUTE.LOGIN.name,
  children: [
    {
      path: APP_ROUTE.DEFAULT,
      loadChildren: () =>
        import('./access/access.module')
          .then((m) => m.AccessModule)
    }
  ]
};

const routes: Routes = [
  /** Legacy Redirects for pages that have been migrated or shorten URLS */
  {
    path: 'parent',
    redirectTo: '/parents/home',
    pathMatch: 'full'
  },
  {
    path: 'admin',
    redirectTo: '/admin/main',
    pathMatch: 'full'
  },
  {
    path: 'join',
    redirectTo: '/student/join',
    pathMatch: 'full'
  },
  {
    path: 'folder/g/:sharefolderid/:folderid',
    redirectTo: '/teacher//gameshows/shared/:sharefolderid/:folderid',
    pathMatch: 'full'
  },
  {
    path: 'folder/m/:sharefolderid/:folderid',
    redirectTo: '/teacher//Missions/shared/:sharefolderid/:folderid',
    pathMatch: 'full'
  },
  {
    path: 'folder/d/:sharefolderid/:folderid',
    redirectTo: '/teacher//Activities/shared/:sharefolderid/:folderid',
    pathMatch: 'full'
  },
  {
    path: 'fourchallenges',
    redirectTo: 'mathleaders/handbook/fourchallengesmathleadersface',
    pathMatch: 'full'
  },
  {
    path: 'getstarted',
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: 'parentemailverify/:VerificationCodeId',
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: 'partneractivity',
    component: PartnerActivityComponent
  },
  {
    path: 'previewquestion/:QuestionId',
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  /** End of Legacy Redirects */

  {
    path: 'resetpassword/:resetcode',
    redirectTo: 'login',
    pathMatch: 'full'
  },

  LOGIN_ROUTE_OBJ,
  {
    path: APP_ROUTE.VERIFY_EMAIL.name,
    component: VerifyEmailComponent
  },
  {
    path: APP_ROUTE.LOGINTOKEN.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () => import('./login-token/login-token.module').then((m) => m.LoginTokenModule)
      }
    ]
  },
  {
    path: 'partner',
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./partner/partner.module')
            .then((m) => m.PartnerModule)
      }
    ]
  },
  {
    path: APP_ROUTE.ADMIN.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./admin/admin.module')
            .then((m) => m.AdminModule)
      }
    ]
  },
  {
    path: APP_ROUTE.IMPACT_PROGRAM.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./impact-program/impact-program.module')
            .then((m) => m.ImpactProgramModule)
      }
    ]
  },
  {
    path: APP_ROUTE.CONFERENCE.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.CONFERENCE.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.CONFERENCE.PRIZERS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.CONFERENCE.WEBINAR.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.CONFERENCE.WEBINAR.CONTENT.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: '',
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: APP_ROUTE.CONFERENCE.WEBINAR_LOAD.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.CONFERENCE.WEBINAR_LOAD.CONTENT.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: '',
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: '',
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.STUDENT.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./student/student.module')
            .then((m) => m.StudentModule)
      }
    ]
  },
  {
    path: APP_ROUTE.BOARD_ADMIN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.BOARD_ADMIN.BOARD_DASHBOARD.name,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: '**',
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      }
    ]
  },
  {
    path: APP_ROUTE.CS_ADMIN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.CS_ADMIN.CREATE_SO.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.CS_ADMIN.DASHBOARD.name,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.CS_ADMIN.DASHBOARD.DISTRICT.name,
            component: EmptyLayoutComponent,
            children: [
              {
                path: APP_ROUTE.CS_ADMIN.DASHBOARD.DISTRICT.BOARD.name,
                component: EmptyLayoutComponent,
                canActivate: mapToCanActivate([ExternalLinkGuard])
              },
              {
                path: APP_ROUTE.CS_ADMIN.DEFAULT,
                component: EmptyLayoutComponent,
                canActivate: mapToCanActivate([ExternalLinkGuard])
              }
            ]
          },
          {
            path: APP_ROUTE.CS_ADMIN.DASHBOARD.OVERALL_PRODUCT.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: APP_ROUTE.CS_ADMIN.DISTRICT.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.CS_ADMIN.DEFAULT,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.CURRICULUM_REVIEWER.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.EDITOR.name,
    children: [
      {
        path: EDITOR_ROUTE.DEFAULT,
        loadChildren: () => import('./editor/editor.module').then((m) => m.EditorModule)
      }
    ]
  },
  {
    path: APP_ROUTE.EXPLORE_ACTIVITIES.name,
    loadChildren: () => import('./explore-activities/explore-activities.module').then((m) => m.ExploreActivitiesModule)
  },
  {
    path: APP_ROUTE.FIREWALL_ADMIN.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.LAUNCH.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.LAUNCH.GETTING_STARTED.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LAUNCH.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LAUNCH.IMPACT_CHALLENGE.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LAUNCH.PORTAL.name,
        children: [
          {
            path: APP_ROUTE.LAUNCH.PORTAL.PORTAL_VIDEO.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: APP_ROUTE.LAUNCH.DEFAULT,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: APP_ROUTE.LAUNCH.SCHOOL_CERTIFIED.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LAUNCH.UPCOMING_WEBINARS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LAUNCH.DEFAULT,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.LEADERSHIP_SERIES.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [

      {
        path: APP_ROUTE.LEADERSHIP_SERIES.ABOUT.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.CONTENT.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.PODCAST.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.PODCASTS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.RESOURCES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.ROUNDTABLES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.VIDEO.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.WEBINAR.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LEADERSHIP_SERIES.WEBINAR_LOAD.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.LICENSE_MANAGER.name,
    component: EmptyLayoutComponent,
    children: [
      {
        path: APP_ROUTE.LICENSE_MANAGER.LICENSES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.LICENSE_MANAGER.SETTINGS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: '',
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.MEDIA_ADMIN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.name,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.EDIT.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.ASSET.name,
            canActivate: mapToCanActivate([ExternalLinkGuard]),
            children: [
              {
                path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.ASSET.EDIT.name,
                component: EmptyLayoutComponent,
                canActivate: mapToCanActivate([ExternalLinkGuard])
              }
            ]
          },
          {
            path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.SECTION.name,
            canActivate: mapToCanActivate([ExternalLinkGuard]),
            children: [
              {
                path: APP_ROUTE.MEDIA_ADMIN.COLLECTION.SECTION.EDIT.name,
                component: EmptyLayoutComponent,
                canActivate: mapToCanActivate([ExternalLinkGuard])
              }
            ]
          }
        ]
      },
      {
        path: APP_ROUTE.MEDIA_ADMIN.EXPERT.name,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.MEDIA_ADMIN.EXPERT.EDIT.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: APP_ROUTE.MEDIA_ADMIN.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MEDIA_ADMIN.TAGS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MEDIA_ADMIN.SETTINGS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.MATH_LEADERS.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.MATH_LEADERS.HANDBOOK.name,
        canActivate: mapToCanActivate([ExternalLinkGuard]),
        children: [
          {
            path: APP_ROUTE.MATH_LEADERS.HANDBOOK.COMMON_BOOK.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: APP_ROUTE.MATH_LEADERS.HANDBOOK.FOUR_CHALLENGES_BOOK.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: APP_ROUTE.MATH_LEADERS.HANDBOOK.RETURN_TO_LEARN_BOOK.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          },
          {
            path: APP_ROUTE.MATH_LEADERS.HANDBOOK.UNDERSTANDING_ROLE_BOOK.name,
            component: EmptyLayoutComponent,
            canActivate: mapToCanActivate([ExternalLinkGuard])
          }
        ]
      },
      {
        path: APP_ROUTE.MATH_LEADERS.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.PODCAST.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.PODCASTS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.READING_LIST.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.ROUNDTABLES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.SPEAKERS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.VIDEO.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.VIDEOS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.WEBINAR.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.WELCOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.MATH_LEADERS.WORKSHOPS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.PARENT_ADMIN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.PARENT_ADMIN.STATISTICS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.PARENTS.name,
    children: [
      {
        path: EDITOR_ROUTE.DEFAULT,
        loadChildren: () => import('./parents/parents.module').then((m) => m.ParentsModule)
      }
    ]
  },
  {
    path: APP_ROUTE.PARENT.name,
    children: [
      {
        path: PARENT_ROUTE.DEFAULT,
        loadChildren: () => import('./parent/parent.module').then((m) => m.ParentModule)
      }
    ]
  },
  {
    path: APP_ROUTE.PARTNERS.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.PARTNERS.DASHBOARD.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.PRINCIPAL_ADMIN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.PRINCIPAL_ADMIN.CREATE_ACCOUNT.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.PRINCIPAL_ADMIN.CREATE_BATCH.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.PRINCIPAL_ADMIN.PREMIUM_CODES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.PURCHASER.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.PURCHASER.CODES.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.QUESTION_CLONER.name,
    children: [
      {
        path: APP_ROUTE.QUESTION_CLONER.EDIT_QUESTIONS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.QUESTION_CLONER.VIEW_QUESTIONS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.QUESTION_CLONER.DEFAULT,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.RETURN_TO_LEARN.name,
    canActivate: mapToCanActivate([ExternalLinkGuard]),
    children: [
      {
        path: APP_ROUTE.RETURN_TO_LEARN.ANALYSIS.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.RETURN_TO_LEARN.HOME.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.RETURN_TO_LEARN.SUPPORT.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      },
      {
        path: APP_ROUTE.RETURN_TO_LEARN.THOUGHT_LEADERSHIP.name,
        component: EmptyLayoutComponent,
        canActivate: mapToCanActivate([ExternalLinkGuard])
      }
    ]
  },
  {
    path: APP_ROUTE.REMEDIATION.name,
    canActivate: mapToCanActivate([AuthGuard]),
    data: {
      allowedRoles: [
        UserRole.teacher,
        UserRole.principal,
        UserRole.vicePrincipal,
        UserRole.regionalLeader,
        UserRole.mathLead,
        UserRole.superintendent,
        UserRole.csAdministrator
      ]
    },
    loadChildren: () => import('./remediation/remediation.module').then((m) => m.RemediationModule)
  },
  {
    path: APP_ROUTE.REVIEWER.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.RFA.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.SCHOOL_ADMIN_APPROVER.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: DASHBOARD_USAGE_ROUTE.name,
    loadChildren: () => import('./dashboard-usage/dashboard-usage.module').then((m) => m.DashboardUsageModule)
  },
  {
    path: 'shop',
    canActivate: mapToCanActivate([AuthGuard]),
    loadChildren: () => import('./shop/shop.module').then((m) => m.ShopModule)
  },
  {
    path: APP_ROUTE.SETTINGS.name,
    loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule)
  },
  {
    path: APP_ROUTE.QUESTION_PREVIEW.name,
    loadChildren: () => import('./question-preview/question-preview.module').then((m) => m.QuestionPreviewModule)
  },
  {
    path: APP_ROUTE.AR.name,
    loadChildren: () => import('./ar/ar.module').then((m) => m.ArModule)
  },
  {
    path: APP_ROUTE.STUDENT_QUESTION_ATTEMPT_ROUTE.name,
    loadChildren: () => import('./student-question-attempt/student-question-attempt.module').then((m) => m.StudentQuestionAttemptModule)
  },
  {
    path: APP_ROUTE.TEACHERS.name,
    canActivate: mapToCanActivate([AuthGuard]),
    loadChildren: () => import('./teachers/teachers.module').then((m) => m.TeachersModule)
  },
  {
    path: APP_ROUTE.DEMO_DISTRICT.name,
    canActivate: mapToCanActivate([DemoSiteGuard]),
    loadChildren: () => import('./demo-district/demo-district.module').then((m) => m.DemoDistrictModule)
  },
  {
    path: APP_ROUTE.BOARD_ADMIN.name,
    loadChildren: () => import('./board-admin/board-admin.module').then((m) => m.BoardAdminModule)
  },
  {
    path: APP_ROUTE.SCHOOL_ADMIN.name,
    loadChildren: () => import('./school-admin/school-admin.module').then((m) => m.SchoolAdminModule)
  },
  {
    path: APP_ROUTE.REGIONAL_ADMIN.name,
    loadChildren: () => import('./regional-admin/regional-admin.module').then((m) => m.RegionalAdminModule)
  },
  {
    path: APP_ROUTE.TEACHER.name,
    loadChildren: () => import('./teacher/teacher.module').then((m) => m.TeacherModule)
  },
  {
    path: APP_ROUTE.UNSUBSCRIBE.name,
    loadChildren: () => import('./unsubscribe/unsubscribe.module').then((m) => m.UnsubscribeModule)
  },
  {
    canActivate: mapToCanActivate([AuthGuard]),
    path: APP_ROUTE.UNSUBSCRIBEGROUP.name,
    loadChildren: () => import('./unsubscribegroup/unsubscribegroup.module').then((m) => m.UnsubscribeGroupModule)
  },
  {
    path: APP_ROUTE.TEACHING_SUPPORTS.name,
    canActivate: mapToCanActivate([AuthGuard]),
    data: {
      allowedRoles: [
        UserRole.teacher,
        UserRole.principal,
        UserRole.vicePrincipal,
        UserRole.regionalLeader,
        UserRole.mathLead,
        UserRole.superintendent,
        UserRole.csAdministrator
      ]
    },
    children: [
      {
        path: APP_ROUTE.TEACHING_SUPPORTS.REMEDIATION.name,
        loadChildren: () => import('./remediation/remediation.module').then((m) => m.RemediationModule)
      }
    ]
  },
  {
    path: APP_ROUTE.REPORTS.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./reports/reports.module')
            .then((m) => m.ReportsModule)
      }
    ]
  },
  {
    path: APP_ROUTE.REGISTER.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./register/register.module')
            .then((m) => m.RegisterModule)
      }
    ]
  },
  {
    path: APP_ROUTE.NOVA_GAME_OR.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([NovaExternalLinkGuard])
  },
  {
    path: APP_ROUTE.NOVA_GAME_ZR.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([NovaExternalLinkGuard])
  },
  {
    path: APP_ROUTE.NOVA_GAME_TF.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([NovaExternalLinkGuard])
  },
  {
    path: APP_ROUTE.NOVA_GAME_ARCADE.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([NovaExternalLinkGuard])
  },
  {
    path: APP_ROUTE.CALENDARS.name,
    component: CalendarEditComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.FORGOT_LOGIN.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    path: APP_ROUTE.FOLDER.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([SharedLinksGuard])
  },
  {
    path: ACCESS_ROUTE.KH_SAML_LOGOUT_REDIRECT.name,
    component: EmptyLayoutComponent,
    canActivate: mapToCanActivate([ExternalLinkGuard])
  },
  {
    component: SamlInternalLogoutComponent,
    path: ACCESS_ROUTE.KH_SAML_INTERNAL_LOGOUT.name,
    canActivate: mapToCanActivate([])
  },
  {
    path: APP_ROUTE.EXTERNAL_URL.name,
    children: [
      {
        path: APP_ROUTE.DEFAULT,
        loadChildren: () =>
          import('./external-url/external-url.module')
            .then((m) => m.ExternalUrlModule)
      }
    ]
  },
  {
    path: APP_ROUTE.ACTIVITY.name,
    redirectTo: 'teacher//content/:publicunitid//view'
  },
  {
    path: APP_ROUTE.PAGE_NOT_FOUND.name,
    component: PageNotFoundComponent
  },
  {
    path: 'oauth',
    children: [
      {
        matcher: (url) => (url.length === 1 && url[0].path.includes('google'))
          ? {
            consumed: url
          }
          : null,
        component: OauthGoogleHandlerComponent
      }
    ]
  },
  {
    path: APP_ROUTE.ACTIVITY_MARKETING.name,
    loadChildren: () => import('./activity-marketing/activity-marketing.module').then((m) => m.ActivityMarketingModule)
  },
  {
    path: 'testavatar',
    component: AsyncAvatarLoadComponent
  },
  {
    path: TEACHERS_ROUTE.MERGE_COMPLETE.name,
    component: MergeCompleteComponent
  },
  {
    path: APP_ROUTE.FORBIDDEN.name,
    pathMatch: 'full',
    component: ForbiddenComponent
  },
  {
    path: '',
    redirectTo: APP_ROUTE.LOGIN.name,
    pathMatch: 'full'
  },
  {
    path: '**',
    component: PageNotFoundComponent
  }
] as Routes;

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    anchorScrolling: 'enabled',
    canceledNavigationResolution: 'replace',
    paramsInheritanceStrategy: 'emptyOnly',
    urlUpdateStrategy: 'deferred'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
